import type { Scene, SceneNodeId } from '../scene/sceneState.js';
import type { DataFieldType, SceneDataContext } from '../graph/dataGraph.js';
import { dataFieldPathLabel } from '../graph/dataSourceSchema.js';
import type { AppState } from './appState.js';

export interface RecordContextFieldOption {
  type: DataFieldType;
  label: string;
}

/** Effective record binding for a node, including a binding inherited from an ancestor frame. */
export function recordContextForSceneNode(
  scene: Scene,
  nodeId: SceneNodeId,
): SceneDataContext | undefined {
  const visit = (ids: readonly SceneNodeId[], inherited?: SceneDataContext): SceneDataContext | undefined => {
    for (const id of ids) {
      const node = scene.nodes[id];
      if (!node) continue;
      const context = node.dataContext ?? inherited;
      if (id === nodeId) return context;
      if (node.kind === 'frame') {
        const nested = visit(node.childIds, context);
        if (nested) return nested;
      }
    }
    return undefined;
  };
  return visit(scene.rootIds);
}

/** Fields available to a reusable graph from owners that currently use it. */
export function recordContextFieldsForGraph(
  appState: AppState,
  graphId: string | null,
): Record<string, RecordContextFieldOption> {
  if (!graphId) return {};
  const sourceIds = new Set<string>();
  const visit = (scene: Scene, ids: string[], inheritedSource?: string, stack = new Set<string>()): void => {
    for (const id of ids) {
      const node = scene.nodes[id];
      if (!node) continue;
      const sourceId = node.dataContext?.source ?? inheritedSource;
      if (node.behavior?.use === graphId && sourceId) sourceIds.add(sourceId);
      if (node.kind === 'frame') visit(scene, node.childIds, sourceId, stack);
      if (node.kind !== 'instance' || stack.has(node.componentId)) continue;
      const component = appState.componentLibrary[node.componentId];
      if (component) visit(component.scene, component.scene.rootIds, sourceId, new Set([...stack, node.componentId]));
    }
  };
  visit(appState.scene, appState.scene.rootIds);

  const options: Record<string, RecordContextFieldOption> = {};
  const conflicts = new Set<string>();
  for (const sourceId of [...sourceIds].sort()) {
    const source = appState.dataSources[sourceId];
    if (!source) continue;
    for (const [fieldId, field] of Object.entries(source.fields)) {
      const existing = options[fieldId];
      if (existing && existing.type !== field.type) {
        conflicts.add(fieldId);
        delete options[fieldId];
        continue;
      }
      if (!conflicts.has(fieldId)) options[fieldId] = {
        type: field.type,
        label: `${dataFieldPathLabel(field.path)} · ${sourceId}`,
      };
    }
  }
  return options;
}
